import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { tw } from 'twind';
import Select, { StylesConfig, createFilter } from 'react-select';
import { Options, PhoneNumberInputStyleConfig } from './types';
import { countriesData, defaultCountry } from './utils';
import { pickerStyles, selectStyles } from './styles';

const dangerColor = '#EB5757';

interface CountryCodeSelectorProps {
  value?: Options;
  onChange: (country: Options) => void;
  hasError?: boolean;
  styleConfig: PhoneNumberInputStyleConfig;
  selectStyleConfig?: StylesConfig<Options, boolean>;
  showFlag?: boolean;
  formatOptionLabel?: (data: Options) => React.ReactNode;
}

export default function CountryCodeSelector({
  value = defaultCountry,
  onChange,
  formatOptionLabel = defaultFormatOptionLabel,
  styleConfig = pickerStyles,
  selectStyleConfig = selectStyles,
  hasError = false,
  showFlag = false,
}: CountryCodeSelectorProps) {
  const [isOpenList, setIsOpenList] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const onSelect = useCallback(
    (option: Options | null) => {
      if (option) {
        setIsOpenList(false);
        onChange(option);
      }
    },
    [onChange, setIsOpenList]
  );

  useEffect(() => {
    const onClickAway = (event: MouseEvent) => {
      if (!selectRef.current?.contains(event.target as Node)) {
        event.stopPropagation();
        setIsOpenList(false);
      }
    };
    if (isOpenList) {
      document.addEventListener('click', onClickAway, { capture: true });
      return () => {
        document.removeEventListener('click', onClickAway, { capture: true });
      };
    }
  }, [isOpenList, setIsOpenList]);

  return (
    <div className={tw`relative`}>
      <button
        type="button"
        onClick={(e) => setIsOpenList(true)}
        className={tw`${styleConfig.buttonClass} ${hasError && `text-[${dangerColor}]`}`}
      >
        {showFlag && <img className={tw`${styleConfig.imgClass}`} src={value!.flag} alt={value!.label} />}
        <span className={tw`${styleConfig.textClass}`}>+{value!.countryCode}</span>
        <div
          className={tw`${styleConfig.indicatorClass} ${isOpenList && 'transform rotate-180'} ${
            hasError && `borderTopError-[${dangerColor}]`
          }`}
        ></div>
      </button>
      {isOpenList && (
        <div className={tw`absolute z-[2]`} ref={selectRef}>
          <Select
            placeholder="Search..."
            isClearable={false}
            autoFocus
            isSearchable
            isMulti={false}
            filterOption={createFilter({ ignoreAccents: false })}
            menuIsOpen
            onChange={onSelect}
            options={countriesData}
            formatOptionLabel={formatOptionLabel}
            styles={selectStyleConfig}
          />
        </div>
      )}
    </div>
  );
}

function defaultFormatOptionLabel({ value, label, flag }: Options) {
  return (
    <div className={tw`flex items-center`}>
      <img alt={label} src={flag} className={tw`w-[25px]`} />
      <div className={tw`ml-2`}>{label}</div>
    </div>
  );
}
